// Skyscanner Downstream Visibility (DV) — Pixel solution.
//
// All Skyscanner DV logic lives in this single module so page/UI code stays thin. Events are sent
// DIRECTLY from the traveller's browser to Skyscanner as an invisible <img> GET to
// `https://analytics.skyscanner.net/dv/v1/event.png?...` (the "pixel" transport). This is separate
// from the deprecated, dormant SAT tag in Result/index.tsx — per Skyscanner's guidance we do NOT
// touch SAT.
//
// The module is null-safe and side-effect-free when no `skyscanner_redirectid` is stored, so organic
// (non-Skyscanner) users see zero behavioral change and zero extra network calls.

import { getQueryParameterByName } from "../utils";

export type SkyscannerStage =
  | "home-page"
  | "dayview-page"
  | "booking-page"
  | "checkout-page"
  | "recheckout-page";

// `source` reported on the reservation checkout payload.
export type BookingSource = "SKYSCANNER" | "WEB";

// URL param appended by Skyscanner on the landing page (note the underscore).
const REDIRECT_PARAM = "skyscanner_redirectid";

// Persistence keys (cookie + localStorage for resilience).
const COOKIE_NAME = "sky_redirect_id";
const LS_ID_KEY = "sky_redirect_id";
const LS_AT_KEY = "sky_redirect_at";

// 30 days — matches Skyscanner's attribution window.
const MAX_AGE_SECONDS = 30 * 24 * 60 * 60; // 2592000

// Dedupe guards.
const SENT_PREFIX = "sky_sent_"; // sessionStorage: + stage -> "1"
const CHECKOUT_COUNT_KEY = "sky_checkout_count"; // sessionStorage
const CONV_PREFIX = "sky_conv_"; // localStorage: + conversionId -> "1"
const TOTAL_PREFIX = "sky_total_"; // localStorage: + reservationId -> "price|currency"
const TOTAL_LAST_KEY = "sky_total_last"; // localStorage: most-recent checkout "price|currency"

// Config (env, with the documented Skyscanner endpoint as the default).
const ENDPOINT =
  process.env.REACT_APP_SKYSCANNER_DV_ENDPOINT ||
  "https://analytics.skyscanner.net/dv/v1/event.png";
const TRACKING_SOURCE_ID =
  process.env.REACT_APP_SKYSCANNER_DV_TRACKING_SOURCE_ID || "DV-422339-1";

let warnedMissingId = false;

// --- internal helpers (not exported) -------------------------------------------------

/** Feature flag default ON; only the literal string "false" disables it. */
function flagEnabled(): boolean {
  return process.env.REACT_APP_SKYSCANNER_DV_ENABLED !== "false";
}

/** Pixels require a tracking_source_id; no-op (with a one-time warning) until it's set. */
function isEnabled(): boolean {
  if (!flagEnabled()) return false;
  if (!TRACKING_SOURCE_ID) {
    if (!warnedMissingId) {
      warnedMissingId = true;
      console.warn(
        "[skyscanner-dv] REACT_APP_SKYSCANNER_DV_TRACKING_SOURCE_ID is not set — pixels disabled."
      );
    }
    return false;
  }
  return true;
}

function nowIso(): string {
  return new Date().toISOString();
}

function writeCookie(value: string): void {
  try {
    const secure =
      typeof window !== "undefined" && window.location?.protocol === "https:"
        ? "; Secure"
        : "";
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(
      value
    )}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* cookies blocked (e.g. Safari private mode) — localStorage still covers us */
  }
}

function readCookie(): string {
  try {
    const match = document.cookie.match(
      new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]*)")
    );
    return match ? decodeURIComponent(match[1]) : "";
  } catch {
    return "";
  }
}

function lsGet(key: string): string {
  try {
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function lsSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage blocked — cookie still covers us */
  }
}

function ssGet(key: string): string {
  try {
    return window.sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function ssSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* no-op */
  }
}

// Keep a transient reference to in-flight pixels so the GC can't drop them mid-request.
const inflight: HTMLImageElement[] = [];

/**
 * Fire-and-forget: render an invisible <img> GET to Skyscanner with the given params.
 * Only defined params are included; URLSearchParams URL-encodes keys and values.
 */
function emitPixel(params: Record<string, string | undefined>): void {
  try {
    const qs = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== "") {
        qs.append(key, value);
      }
    });

    const img = new Image(1, 1);
    img.style.cssText = "width:1px;height:1px;border-style:none;"; // NOT display:none (Skyscanner doc)
    img.referrerPolicy = "no-referrer-when-downgrade";
    const release = () => {
      const i = inflight.indexOf(img);
      if (i !== -1) inflight.splice(i, 1);
    };
    img.onload = release;
    img.onerror = () => {
      // Skyscanner doc: log errors that suggest the pixel did not load correctly.
      console.warn("[skyscanner-dv] pixel failed to load");
      release();
    };
    inflight.push(img);
    img.src = `${ENDPOINT}?${qs.toString()}`;
  } catch {
    /* swallow — tracking must never break or block the UI */
  }
}

function countByAge(travelers: any[], category: string): string {
  return String(
    travelers.filter(
      (t) => String(t?.ageCategory || "").toUpperCase() === category
    ).length
  );
}

/**
 * Grand total as a 2-decimal string (transaction_total is REQUIRED by Skyscanner). The reservation
 * object isn't always fully populated right after payment, so fall back through several fields and
 * finally sum the per-traveler totals. Returns undefined only if no positive total can be found.
 */
function readReservationTotal(reservation: any, travelers: any[]): string | undefined {
  const candidates = [
    reservation?.totalAmount,
    reservation?.reservationTotalPrice,
    reservation?.amount,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (c != null && c !== "" && Number.isFinite(n) && n > 0) return n.toFixed(2);
  }
  const sum = (travelers || []).reduce(
    (acc: number, t: any) => acc + (Number(t?.totalPrice) || 0),
    0
  );
  return sum > 0 ? sum.toFixed(2) : undefined;
}

// --- public API ----------------------------------------------------------------------

/**
 * Read `skyscanner_redirectid` from the current URL and persist it (cookie + localStorage) with a
 * capture timestamp. "Last click wins": a newer id overwrites an older one. No-op when absent.
 */
function captureFromUrl(): void {
  const id = getQueryParameterByName(REDIRECT_PARAM); // "" when absent
  if (!id) return;
  if (id === getRedirectId()) return; // unchanged -> avoid needless rewrites

  writeCookie(id);
  lsSet(LS_ID_KEY, id);
  lsSet(LS_AT_KEY, String(Date.now()));
}

/** The stored redirect id (cookie first, then localStorage). "" when none stored. */
function getRedirectId(): string {
  return readCookie() || lsGet(LS_ID_KEY);
}

/** When the stored redirect id was captured (epoch ms), or null. */
function getStoredAt(): number | null {
  const raw = lsGet(LS_AT_KEY);
  const n = Number(raw);
  return raw && !Number.isNaN(n) ? n : null;
}

/** True while the stored redirect id is inside Skyscanner's 30-day attribution window. */
function isWithinAttributionWindow(): boolean {
  const at = getStoredAt();
  // No timestamp (e.g. localStorage blocked, so we're reading the cookie): the cookie's own
  // Max-Age already bounds it to 30 days, so trust it rather than drop a real Skyscanner booking.
  if (at === null) return true;
  return Date.now() - at <= MAX_AGE_SECONDS * 1000;
}

/**
 * The `source` to send on the reservation checkout payload. "SKYSCANNER" when the traveller
 * arrived via Skyscanner, otherwise "WEB". Two signals, in order:
 *
 *  1. A stored `skyscanner_redirectid` still inside the 30-day window — the durable one. It's
 *     captured before React renders (index.tsx) into a cookie + localStorage, so it survives a
 *     refresh, a re-search from the homepage widget and the Stripe round-trip. The URL param does
 *     not: it only ever hitches a ride on the next/back links between /booking/* pages.
 *  2. `?source=SKYSCANNER` on the current URL — the legacy param, kept so a manually tagged link
 *     still wins when both cookies and localStorage are blocked.
 *
 * Deliberately NOT gated by REACT_APP_SKYSCANNER_DV_ENABLED: that flag controls whether we emit
 * pixels TO Skyscanner, whereas this is commercial attribution stored on our own reservation.
 * `document.referrer` is deliberately not a signal — by the time checkout runs it's our own origin.
 */
function getBookingSource(): BookingSource {
  if (getRedirectId() && isWithinAttributionWindow()) return "SKYSCANNER";
  if ((getQueryParameterByName("source") || "").toUpperCase() === "SKYSCANNER") {
    return "SKYSCANNER";
  }
  return "WEB";
}

/**
 * Fire a pre-conversion pixel for a funnel stage. Only when enabled and a redirect id is stored.
 * De-duped once per stage per session (recheckout-page is exempt so each price refresh reports).
 */
function sendPreConversion(
  stage: SkyscannerStage,
  opts?: { price?: string; currency?: string }
): void {
  if (!isEnabled()) return;
  const redirectId = getRedirectId();
  if (!redirectId) return;

  const sentKey = SENT_PREFIX + stage;
  if (stage !== "recheckout-page" && ssGet(sentKey) === "1") return;
  ssSet(sentKey, "1");

  emitPixel({
    skyscanner_redirectid: redirectId,
    tracking_source_id: TRACKING_SOURCE_ID,
    event_type: "pre-conversion",
    occurred_at: nowIso(),
    stage,
    platform: "web",
    price: opts?.price,
    currency: opts?.currency,
  });
}

/**
 * Map the current path to its funnel stage and fire the pre-conversion pixel.
 * - home-page: no price required (Skyscanner doesn't require it for the landing stage).
 * - dayview-page / booking-page: Skyscanner REQUIRES price + currency, sourced from the live price
 *   preview (`pricePreviewDetails`). These only fire once the preview total is known — when called
 *   before that (e.g. on first entry to the flights list), this no-ops and re-runs when price arrives.
 * Deliberately does NOT map /booking/overview — checkout/recheckout fire from sendCheckoutStage.
 */
function sendFunnelStage(pathname: string, previewDetails?: any): void {
  if (pathname === "/") {
    sendPreConversion("home-page");
    return;
  }

  const amount = Number(previewDetails?.reservationTotalPrice);
  const currency = previewDetails?.currencyResponse?.isoCode;
  const priceReady = Number.isFinite(amount) && amount > 0 && !!currency;
  if (!priceReady) return; // wait until the preview total is known; the caller re-runs then

  const opts = { price: amount.toFixed(2), currency: String(currency) };
  if (pathname.startsWith("/booking/flights")) {
    sendPreConversion("dayview-page", opts);
  } else if (pathname.startsWith("/booking/passengers")) {
    sendPreConversion("booking-page", opts);
  }
}

/**
 * Fire the payment-page pre-conversion pixel after a successful checkout response (price+currency
 * known). First checkout this session -> checkout-page; a refreshed-price re-run (e.g. coupon
 * applied) -> recheckout-page. The counter lives in sessionStorage so it survives remounts.
 */
function sendCheckoutStage(args: { price?: string; currency?: string }): void {
  const n = Number(ssGet(CHECKOUT_COUNT_KEY) || "0") || 0;
  ssSet(CHECKOUT_COUNT_KEY, String(n + 1));
  const stage: SkyscannerStage = n === 0 ? "checkout-page" : "recheckout-page";
  sendPreConversion(stage, { price: args.price, currency: args.currency });
}

/**
 * Persist the checkout total (keyed by reservation id) so the conversion can report it. The reservation
 * fetched right after payment often hasn't had its price fields populated yet, but at checkout time we
 * always have the authoritative total (it's what created the Stripe session). Survives the Stripe
 * round-trip via localStorage.
 */
function rememberCheckoutTotal(
  reservationId: string | undefined,
  price: string | undefined,
  currency: string | undefined
): void {
  if (!price) return;
  const value = `${price}|${currency || ""}`;
  if (reservationId) lsSet(TOTAL_PREFIX + reservationId, value);
  lsSet(TOTAL_LAST_KEY, value); // safety net if the return-page id doesn't string-match
}

/**
 * Fire the conversion pixel on payment confirmation. Only when enabled and a redirect id is stored.
 * De-duped by conversion id (localStorage) so a page refresh doesn't double-count.
 * `reservation` is the UserBooking-shaped object returned by getReservationById; `reservationId` keys
 * the checkout total persisted by rememberCheckoutTotal (the reliable source for transaction_total).
 */
function sendConversion(reservation: any, reservationId?: string): void {
  if (!isEnabled()) return;
  const redirectId = getRedirectId();
  if (!redirectId) return;
  if (!reservation) return;

  const conversionId =
    reservation.pnrCode ||
    (reservation.confirmationCode != null
      ? String(reservation.confirmationCode)
      : "") ||
    reservation.id ||
    "";
  if (!conversionId) return;

  const convKey = CONV_PREFIX + conversionId;
  if (lsGet(convKey) === "1") return; // already reported this booking
  lsSet(convKey, "1");

  const travelers: any[] = Array.isArray(reservation.travelers)
    ? reservation.travelers
    : [];
  const firstTraveler = travelers[0];
  const routeType = firstTraveler?.returningFlight ? "return" : "one-way";

  // transaction_total is REQUIRED. Try the reservation first, then fall back to the total persisted
  // at checkout (the post-payment reservation often hasn't populated its price fields yet).
  const stored =
    (reservationId ? lsGet(TOTAL_PREFIX + reservationId) : "") || lsGet(TOTAL_LAST_KEY);
  const [storedPrice, storedCurrency] = stored ? stored.split("|") : ["", ""];
  const storedTotal =
    storedPrice && Number(storedPrice) > 0 ? Number(storedPrice).toFixed(2) : undefined;
  const transactionTotal = readReservationTotal(reservation, travelers) || storedTotal;
  const currency = reservation?.currency?.isoCode || storedCurrency || undefined;

  // `legs` and `flight_fare` are intentionally omitted: the reservation's dates are DD/MM/YYYY with
  // unreliable times (not the ISO-8601 `legs` requires), and there's no clean ancillary-excluded
  // fare to populate `flight_fare`. Both are optional — sending invalid values fails validation.
  emitPixel({
    skyscanner_redirectid: redirectId,
    tracking_source_id: TRACKING_SOURCE_ID,
    event_type: "conversion",
    occurred_at: nowIso(),
    conversion_id: String(conversionId),
    transaction_total: transactionTotal,
    currency: currency,
    payment_method: reservation?.paymentMethod?.name || undefined,
    route_type: routeType,
    adult_number: countByAge(travelers, "ADULT"),
    child_number: countByAge(travelers, "CHILD"),
    infant_number: countByAge(travelers, "INFANT"),
    platform: "web",
  });

  // Tidy up the persisted totals now that they've been reported.
  try {
    if (reservationId) window.localStorage.removeItem(TOTAL_PREFIX + reservationId);
    window.localStorage.removeItem(TOTAL_LAST_KEY);
  } catch {
    /* no-op */
  }
}

export const skyscannerPixel = {
  captureFromUrl,
  getRedirectId,
  getStoredAt,
  getBookingSource,
  sendPreConversion,
  sendFunnelStage,
  sendCheckoutStage,
  rememberCheckoutTotal,
  sendConversion,
};

export default skyscannerPixel;
